/**
 * VoiceIntakeModal — voice-dictated CRM intake (Task #70, Jun 9 2026).
 *
 * Manny (sales rep) P1 requirement. Mobile-first per Daniel:
 *   - Ships to Capacitor iOS + Android for the sales / PEM / physician /
 *     nurse mobile apps. Web Speech API works in both Capacitor WebViews.
 *   - Touch-friendly: large record button, big mic icon, modal sized
 *     for narrow phone viewports as well as desktop.
 *
 * Flow:
 *   1. User taps "Voice intake" button -> modal opens
 *   2. Microphone permission requested (auto if browser already granted)
 *   3. Tap record -> Web Speech API streams transcript live
 *   4. Tap stop -> transcript sent to crm:intake:parse-dictation
 *   5. Bedrock returns structured {first_name, last_name, phone, email, notes}
 *   6. User reviews / edits in confirmation form
 *   7. Tap save -> crm:contacts:create with the cleaned fields
 *
 * Permission required: crm:create (server enforces; we just hide the
 * "Voice intake" launcher button when the user lacks it).
 */
import React, { useState, useRef, useEffect, useCallback } from "react";
import ReferralAnswerField from "./ReferralAnswerField";
import { useNavStep } from "../lib/NavHistory";
import {
  Mic, MicOff, Square, X, CheckCircle2, AlertTriangle, Loader2,
  Edit3, Save, Sparkles, RotateCcw,
} from "lucide-react";

// P193 — the ONE dictation hook. Records with getUserMedia + MediaRecorder and
// sends the audio to OUR backend for Transcribe Medical, inside the BAA. The
// browser speech API that used to live here was removed by #762 and is kept out
// by tests/unit/security/noBrowserSpeechApi.test.js.
import useDictation from "../lib/useDictation";

const STEP_PERMISSION = "permission";
const STEP_RECORDING  = "recording";
const STEP_PARSING    = "parsing";
const STEP_CONFIRM    = "confirm";
const STEP_SAVING     = "saving";
const STEP_DONE       = "done";

export default function VoiceIntakeModal({ open, onClose, onSaved }) {
  // #65 Phase C1: global Back closes this overlay first. Unconditional hook,
  // generic label, never PHI.
  useNavStep({ active: open, label: 'Voice intake', onBack: () => onClose && onClose() });
  const [step, setStep] = useState(STEP_PERMISSION);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [fields, setFields] = useState({
    first_name: "", last_name: "", phone: "", email: "", notes: "",
  });
  const [phoneRaw, setPhoneRaw] = useState(null);
  const [emailRaw, setEmailRaw] = useState(null);
  const [aiConfidence, setAiConfidence] = useState(null);
  const [error, setError] = useState(null);
  // Card 27: the referral question is required at creation (Daniel 2026-08-01).
  const [referral, setReferral] = useState({ answer: "", partnerId: "" });
  const [createdContactId, setCreatedContactId] = useState(null);

  const recognitionRef = useRef(null);
  const finalTextRef = useRef("");


  // ─── Lifecycle: reset on open/close ──────────────────────────────
  useEffect(() => {
    if (!open) {
      // Stop any in-flight recognition when the modal closes.
      stopRecognition();
      return;
    }
    // Reset state on each open
    setStep(STEP_PERMISSION);
    setTranscript("");
    setInterimTranscript("");
    finalTextRef.current = "";
    setFields({ first_name: "", last_name: "", phone: "", email: "", notes: "" });
    setPhoneRaw(null);
    setEmailRaw(null);
    setAiConfidence(null);
    setError(null);
    setCreatedContactId(null);
    // Deps array intentionally only `open` - this effect is the
    // open/close lifecycle reset, not a reaction to inner state. The
    // `stopRecognition` closure is stable across renders for our
    // purpose (it reads recognitionRef.current which is itself a ref).
  }, [open]);

  function stopRecognition() {
    const rec = recognitionRef.current;
    if (rec) {
      try { rec.onresult = null; rec.onerror = null; rec.onend = null; rec.stop(); } catch (_) { /* noop */ }
      recognitionRef.current = null;
    }
  }

  // ─── Step 1: request mic permission via getUserMedia (Capacitor-safe) ──
  // Web Speech API on iOS Safari (Capacitor WebView) will silently fail
  // if mic permission has never been requested. Doing getUserMedia first
  // forces the OS-level permission prompt. We then release the stream.
  const requestMic = useCallback(async () => {
    setError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setError("This device doesn't support microphone access. Use Chrome, Safari, or the ZignaGenix mobile app.");
      return false;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Release immediately — Web Speech will reacquire when it starts.
      stream.getTracks().forEach(t => t.stop());
      return true;
    } catch (e) {
      setError(e.name === "NotAllowedError"
        ? "Microphone permission denied. Open device settings to allow microphone for ZignaGenix."
        : `Microphone error: ${e.message}`);
      return false;
    }
  }, []);

  /*
   * ── ⛔⛔ WEB SPEECH REMOVED — DANIEL'S RULING, 30 Oct 2026 (Card P183) ─────
   *
   * This step used to call window.SpeechRecognition / webkitSpeechRecognition.
   * THAT SHIPS THE AUDIO OFF-DEVICE: Chrome streams it to Google, Safari and
   * iOS to Apple. Neither is covered by our BAA, and the text staff dictate
   * here is a prospect's NAME, PHONE, EMAIL and what they are calling about —
   * see docs/transcribe_medical_design_2026-06-07.md §1.3.
   *
   * ⚠ IT WAS NOT A DEGRADED FEATURE, IT WAS AN UNCOVERED DISCLOSURE, so it is
   * removed rather than feature-flagged: a flag can be turned back on, and the
   * whole point is that this path must not exist in shipped code. A CI guard
   * (tests/unit/security/noBrowserSpeechApi.test.js) fails the build if the
   * symbols reappear anywhere under src/ or portal/.
   *
   * ⚠ NOTHING ELSE IN THIS MODAL IS DELETED. The parse → confirm → save steps
   * take a TRANSCRIPT STRING and know nothing about where it came from, so
   * Card P183 PR 2 can hand them a transcript from Transcribe Medical and
   * reuse this flow unchanged. Rewriting them would be the second
   * implementation the card forbids.
   *
   * Intake remains fully usable: "Add Contact" beside the voice button is the
   * typing path and is untouched.
   */

  // ─── Step 3: stop + parse via Bedrock ──────────────────────────────
  /**
   * Parse a transcript into intake fields. P193 extracted this from
   * `stopAndParse`, which read the Web Speech refs directly — it now takes the
   * TEXT, so it works for a Transcribe Medical transcript, a retry, or a paste.
   * ⚠ Unchanged otherwise: same channel, same fallback of dropping the raw
   * transcript into notes when the parse fails, same CONFIRM step at the end.
   * Nothing is saved here.
   */
  const parseTranscript = useCallback(async (text) => {
    const fullTranscript = String(text || "").trim();
    setTranscript(fullTranscript);
    setInterimTranscript("");

    if (!fullTranscript || fullTranscript.length < 5) {
      setError("Recording was too short. Tap record and try again.");
      setStep(STEP_PERMISSION);
      return;
    }

    setStep(STEP_PARSING);
    setError(null);

    try {
      const r = await window.zignagenixAPI.invoke("crm:intake:parse-dictation", {
        transcript: fullTranscript,
      });
      if (r && r.error) throw new Error(r.message || r.code);
      const f = r.fields || {};
      setFields({
        first_name: f.first_name || "",
        last_name: f.last_name || "",
        phone: f.phone || "",
        email: f.email || "",
        notes: f.notes || "",
      });
      setPhoneRaw(f.phone_raw || null);
      setEmailRaw(f.email_raw || null);
      setAiConfidence(r.ai_confidence || null);
      if (r.ai_unavailable && r.ai_notes) setError(r.ai_notes);
      setStep(STEP_CONFIRM);
    } catch (e) {
      setError(`Parse failed: ${e.message}. Edit fields manually below.`);
      // Still let the user save manually with the raw transcript in notes
      setFields(f => ({ ...f, notes: fullTranscript }));
      setStep(STEP_CONFIRM);
    }
  }, []);

  /*
   * ⭐ P193 — dictation restored, on the Transcribe Medical batch path.
   * ⚠ The transcript is handed to the SAME parse → confirm → save steps that
   * were already here; #762 deliberately left them untouched for this. Nothing
   * is saved without the user's confirm, and the parsed fields stay editable.
   */
  const mic = useDictation({
    send: (payload) => (window.zignagenixAPI && window.zignagenixAPI.dictateIntake
      ? window.zignagenixAPI.dictateIntake(payload)
      : Promise.resolve({ error: true, message: "Dictation is unavailable in this build." })),
    onTranscript: (t) => {
      if (!t) { setStep(STEP_PERMISSION); return; }
      finalTextRef.current = t;
      setTranscript(t);
      parseTranscript(t);
    },
  });

  // ─── Step 4: save the contact ───────────────────────────────────────
  const save = useCallback(async () => {
    if (!fields.first_name && !fields.last_name && !fields.phone && !fields.email) {
      setError("At least one of name, phone, or email is required.");
      return;
    }
    // Card 27: not optional, not skippable — server refuses without it too
    // (REFERRAL_ANSWER_REQUIRED), and migration 161's trigger backstops the server.
    if (referral.answer !== "none" && referral.answer !== "partner") {
      setError("Answer the referral question: a partner organization, or explicitly 'None / direct'.");
      return;
    }
    if (referral.answer === "partner" && !referral.partnerId) {
      setError("Select the partner organization.");
      return;
    }
    setStep(STEP_SAVING);
    setError(null);
    try {
      const r = await window.zignagenixAPI.createCRMContact({
        first_name: fields.first_name || null,
        last_name: fields.last_name || null,
        phone: fields.phone || null,
        email: fields.email || null,
        notes: fields.notes || null,
        contact_type: "prospect",
        lead_temperature: "warm",
        referralAnswer: referral.answer,
        referralPartnerId: referral.answer === "partner" ? parseInt(referral.partnerId, 10) : null,
      });
      if (r && r.error) throw new Error(r.message || r.code);
      setCreatedContactId(r?.id || r?.contactId || true);
      setStep(STEP_DONE);
      setTimeout(() => {
        if (typeof onSaved === "function") onSaved(r?.id || r?.contactId || null);
        onClose && onClose();
      }, 1500);
    } catch (e) {
      setError(`Save failed: ${e.message}`);
      setStep(STEP_CONFIRM);
    }
  }, [fields, referral, onClose, onSaved]);

  const restart = useCallback(() => {
    setStep(STEP_PERMISSION);
    setTranscript(""); setInterimTranscript("");
    finalTextRef.current = "";
    setFields({ first_name: "", last_name: "", phone: "", email: "", notes: "" });
    setPhoneRaw(null); setEmailRaw(null); setAiConfidence(null); setError(null);
    setReferral({ answer: "", partnerId: "" });
  }, []);

  if (!open) return null;

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000,
      display: "flex", alignItems: "flex-end", justifyContent: "center",
      // On mobile we slide up from the bottom (more thumb-reachable);
      // on desktop the centered look still works because the modal
      // top-level alignSelf is auto. Adding 'sm:items-center' equivalent
      // via media query in JS would require react-responsive — we live
      // with bottom-anchored on mobile, which is actually preferred UX.
    }}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "white", borderRadius: "16px 16px 0 0",
          width: "100%", maxWidth: 520,
          maxHeight: "92vh", overflowY: "auto",
          paddingBottom: 24,
        }}
      >
        {/* Drag handle (mobile bottom sheet aesthetic) */}
        <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 0" }}>
          <div style={{ width: 36, height: 4, background: "#DEE2E6", borderRadius: 2 }} />
        </div>

        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", padding: "12px 20px",
          borderBottom: "1px solid #F1F3F5",
        }}>
          <Sparkles size={18} color="#2E8B98" style={{ marginRight: 8 }} />
          <div style={{ fontSize: 16, fontWeight: 700 }}>Voice intake</div>
          <div style={{ marginLeft: "auto" }}>
            <button onClick={onClose} style={ghostBtn()} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Step body */}
        <div style={{ padding: 20 }}>
          {error && (
            <div style={{
              padding: "10px 12px", borderRadius: 8, marginBottom: 12,
              background: "#FEE2E2", color: "#991B1B", fontSize: 13,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <AlertTriangle size={14} /> {error}
            </div>
          )}

          {step === STEP_PERMISSION && (
            <div style={{ textAlign: "center", padding: "12px 0" }}>
              <div style={{ fontSize: 13, color: "#6C757D", marginBottom: 24 }}>
                Voice intake is paused while dictation moves to our own
                transcription service. Nothing is lost &mdash; use
                <strong> Add Contact</strong> to enter the prospect's details.
              </div>
              {/*
                ⛔ NO DEAD BUTTON. The record control is DISABLED rather than
                removed, so a rep who knows the feature sees where it went and
                why, instead of hunting for a control that silently vanished.
                It carries no onClick at all — a disabled button that still
                wires a handler is one CSS change away from being live again.
              */}
              {/*
                * ⭐ P193 — the mic is LIVE again, on Transcribe Medical inside
                * the BAA. #762 left this as a disabled button saying "Voice
                * coming soon"; the service it was waiting for now exists.
                * ⚠ Still absent, not disabled, when the browser cannot record
                * or the bridge is missing — a button that exists must do
                * something.
                */}
              {mic.available ? (
                <>
                  <button
                    onClick={mic.state === 'recording' ? mic.stop : mic.start}
                    disabled={mic.state === 'transcribing'}
                    aria-pressed={mic.state === 'recording'}
                    style={bigRecordBtn(mic.state === 'recording' ? "#DC3545" : "#212529")}
                    aria-label={mic.state === 'recording' ? "Stop recording" : "Start voice intake"}
                    data-testid="intake-mic"
                  >
                    <Mic size={36} />
                  </button>
                  <div style={{ marginTop: 12, fontSize: 12, color: "#6C757D", fontWeight: 600 }}>
                    {mic.state === 'recording' ? `Recording — tap to stop (${mic.elapsed}s)`
                      : mic.state === 'transcribing' ? 'Transcribing…'
                      : 'Tap to dictate'}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 11, color: "#ADB5BD", lineHeight: 1.5 }}>
                    Max {mic.MAX_CLIP_SECONDS}s. The transcript appears below for you to
                    check and edit — nothing is saved until you confirm.
                  </div>
                  {mic.error ? (
                    <div data-testid="intake-mic-error" role="alert"
                         style={{ marginTop: 10, fontSize: 12, color: "#B91C1C" }}>
                      {mic.error}
                    </div>
                  ) : null}
                </>
              ) : (
                <div style={{ marginTop: 12, fontSize: 12, color: "#6C757D" }}>
                  This device cannot record audio. Use <strong>Add Contact</strong> to type
                  the details.
                </div>
              )}
            </div>
          )}

          {step === STEP_RECORDING && (
            <div>
              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <button onClick={mic.stop} style={bigRecordBtn("#212529")} aria-label="Stop recording">
                  <Square size={32} fill="white" />
                </button>
                <div style={{ marginTop: 10, fontSize: 12, color: "#DC3545", fontWeight: 600 }}>
                  <span style={{
                    display: "inline-block", width: 8, height: 8,
                    background: "#DC3545", borderRadius: "50%",
                    marginRight: 6, animation: "pulse 1s infinite",
                  }} />
                  Recording &mdash; tap to stop
                </div>
              </div>
              <div style={{
                background: "#F8F9FA", borderRadius: 8, padding: 12,
                minHeight: 100, fontSize: 13, lineHeight: 1.5,
              }}>
                <span style={{ color: "#212529" }}>{transcript}</span>
                <span style={{ color: "#ADB5BD", fontStyle: "italic" }}>{interimTranscript}</span>
                {!transcript && !interimTranscript && (
                  <span style={{ color: "#ADB5BD" }}>(Start speaking — transcript appears here…)</span>
                )}
              </div>
            </div>
          )}

          {step === STEP_PARSING && (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Loader2 size={36} className="spin" color="#2E8B98" />
              <div style={{ marginTop: 16, fontSize: 13, color: "#6C757D" }}>
                Parsing your dictation into contact fields…
              </div>
            </div>
          )}

          {step === STEP_CONFIRM && (
            <div>
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                marginBottom: 12,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#6C757D" }}>
                  REVIEW &amp; CONFIRM
                </div>
                {aiConfidence && (
                  <span style={{
                    fontSize: 10, padding: "2px 8px", borderRadius: 999,
                    background: aiConfidence === "high" ? "#DCFCE7" : aiConfidence === "low" ? "#FEE2E2" : "#FEF3C7",
                    color: aiConfidence === "high" ? "#166534" : aiConfidence === "low" ? "#991B1B" : "#92400E",
                    fontWeight: 600,
                  }}>
                    AI {aiConfidence} confidence
                  </span>
                )}
              </div>
              <div style={{ display: "grid", gap: 8, marginBottom: 12 }}>
                <ReferralAnswerField answer={referral.answer} partnerId={referral.partnerId}
                  onChange={setReferral} />
              </div>

              <Field
                label="First name"
                value={fields.first_name}
                onChange={v => setFields(f => ({ ...f, first_name: v }))}
              />
              <Field
                label="Last name"
                value={fields.last_name}
                onChange={v => setFields(f => ({ ...f, last_name: v }))}
              />
              <Field
                label="Phone"
                value={fields.phone}
                onChange={v => setFields(f => ({ ...f, phone: v }))}
                type="tel"
                hint={phoneRaw ? `AI heard: "${phoneRaw}" (couldn't normalize — please confirm)` : null}
              />
              <Field
                label="Email"
                value={fields.email}
                onChange={v => setFields(f => ({ ...f, email: v }))}
                type="email"
                hint={emailRaw ? `AI heard: "${emailRaw}" (couldn't validate — please confirm)` : null}
              />
              <div style={{ marginTop: 14 }}>
                <label style={fieldLabel()}>Notes</label>
                <textarea
                  value={fields.notes}
                  onChange={e => setFields(f => ({ ...f, notes: e.target.value }))}
                  rows={5}
                  style={fieldTextarea()}
                  placeholder="Condition, urgency, lead source, anything else the rep said…"
                />
              </div>

              <div style={{ marginTop: 18, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button onClick={restart} style={ghostBtn()}>
                  <RotateCcw size={14} /> Re-record
                </button>
                <button onClick={save} style={primaryBtn()} disabled={false}>
                  <Save size={14} /> Save contact
                </button>
              </div>

              {transcript && (
                <details style={{ marginTop: 14, fontSize: 11, color: "#6C757D" }}>
                  <summary style={{ cursor: "pointer" }}>Show raw transcript</summary>
                  <div style={{
                    marginTop: 8, padding: 10, background: "#F8F9FA",
                    borderRadius: 6, lineHeight: 1.5,
                  }}>
                    {transcript}
                  </div>
                </details>
              )}
            </div>
          )}

          {step === STEP_SAVING && (
            <div style={{ textAlign: "center", padding: 40 }}>
              <Loader2 size={36} className="spin" color="#2E8B98" />
              <div style={{ marginTop: 16, fontSize: 13, color: "#6C757D" }}>
                Saving contact…
              </div>
            </div>
          )}

          {step === STEP_DONE && (
            <div style={{ textAlign: "center", padding: 30 }}>
              <CheckCircle2 size={48} color="#62BB46" />
              <div style={{ marginTop: 12, fontSize: 15, fontWeight: 600, color: "#212529" }}>
                Contact saved
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: "#6C757D" }}>
                {fields.first_name} {fields.last_name}
              </div>
            </div>
          )}
        </div>

        {/* Inline keyframes for the recording-pulse dot */}
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50%      { opacity: 0.3; }
          }
        `}</style>
      </div>
    </div>
  );
}

// ─── Field + style helpers ────────────────────────────────────────────────
function Field({ label, value, onChange, type = "text", hint }) {
  return (
    <div style={{ marginTop: 12 }}>
      <label style={fieldLabel()}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={fieldInput()}
      />
      {hint && <div style={{ fontSize: 10, color: "#F26522", marginTop: 3 }}>{hint}</div>}
    </div>
  );
}
function fieldLabel() {
  return { display: "block", fontSize: 11, fontWeight: 600, color: "#6C757D", marginBottom: 4 };
}
function fieldInput() {
  return {
    width: "100%", padding: "10px 12px", fontSize: 15,
    borderRadius: 8, border: "1px solid #CED4DA",
    boxSizing: "border-box",
    // Larger touch target on mobile (44px iOS HIG)
    minHeight: 44,
  };
}
function fieldTextarea() {
  return {
    width: "100%", padding: "10px 12px", fontSize: 14,
    borderRadius: 8, border: "1px solid #CED4DA",
    fontFamily: "inherit", resize: "vertical",
    boxSizing: "border-box", lineHeight: 1.5,
  };
}
function primaryBtn() {
  return {
    background: "#2E8B98", color: "white", border: "none",
    padding: "12px 24px", borderRadius: 10, fontSize: 14, fontWeight: 600,
    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
    minHeight: 48, // 48px = comfortable touch target
    flex: 1,
  };
}
function ghostBtn() {
  return {
    background: "white", border: "1px solid #DEE2E6", color: "#212529",
    padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600,
    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
    minHeight: 44,
  };
}
function bigRecordBtn(color) {
  return {
    background: color, color: "white", border: "none",
    width: 96, height: 96, borderRadius: "50%",
    cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
    // Touch-target compliance + thumb reach
  };
}
